import { useEffect, useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, Loader2, ShieldCheck, Calendar, User, Users, Award, Clock } from "lucide-react";
import { API } from "../App";

const COURSE_LABELS = {
  primeiros_socorros: "Primeiros Socorros",
  epi_nr06: "EPI / NR 06",
  integracao: "Integração",
  combate_incendio: "Combate a Incêndio",
  cipa: "CIPA – Designado",
  direcao_defensiva: "Direção Defensiva",
};

export default function PublicListValidation() {
  const { id } = useParams();
  const location = useLocation();
  // The OTP attendance-list flow ("Lista Digital") uses doc_ids like
  // NR06-LP-2026-000231 encoded in the QR as /validar-lista/{doc_id}.
  // The legacy rubrica lists ("Lista com Rubrica") use list UUIDs at
  // /verificar-lista/{list_id}. Both hit different backend endpoints
  // and return slightly different payload shapes — normalized below.
  const isDocIdRoute = location.pathname.startsWith("/validar-lista");
  const [state, setState] = useState({ loading: true, error: null, data: null });

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const endpoint = isDocIdRoute
          ? `${API}/public/validar-lista/${id}`
          : `${API}/verificar-lista/${id}`;
        const { data: raw } = await axios.get(endpoint);
        // Normalize doc_id endpoint response into the shape the UI expects.
        const data = isDocIdRoute
          ? {
              course_titulo: raw.course,
              course_key: null,
              training_date: raw.training_date,
              created_at: raw.closed_at,
              count: (raw.signers || []).length,
              participants: (raw.signers || []).map((s) => ({
                nome: s.nome,
                cpf_masked: (s.cpf || "").replace(
                  /^(\d{3})\d{5}(\d{3})$/,
                  "$1.***.***-$2",
                ) || s.cpf,
              })),
              certificates: [],
              doc_id: raw.doc_id,
              signed_count: raw.signed_count,
              pending_count: raw.pending_count,
            }
          : raw;
        if (mounted) setState({ loading: false, error: null, data });
      } catch (e) {
        const msg = e?.response?.data?.detail || "Falha ao validar lista";
        if (mounted) setState({ loading: false, error: msg, data: null });
      }
    })();
    return () => { mounted = false; };
  }, [id, isDocIdRoute]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-4 py-12">
      <div className="max-w-3xl mx-auto" data-testid="list-validation-card">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          <div className="bg-gradient-to-r from-indigo-600 to-blue-600 px-6 py-5 flex items-center gap-3 text-white">
            <ShieldCheck className="w-7 h-7" />
            <div>
              <p className="text-xs uppercase tracking-wider opacity-90">AVI Segurança</p>
              <h1 className="text-xl font-semibold">Validação de Lista de Treinamento</h1>
            </div>
          </div>

          <div className="p-6 sm:p-8">
            {state.loading && (
              <div className="flex flex-col items-center py-12 gap-3" data-testid="list-validation-loading">
                <Loader2 className="w-10 h-10 animate-spin text-slate-500" />
                <p className="text-slate-600">Verificando autenticidade…</p>
              </div>
            )}

            {!state.loading && state.error && (
              <div className="text-center py-10" data-testid="list-validation-invalid">
                <XCircle className="w-16 h-16 text-rose-500 mx-auto mb-3" />
                <h2 className="text-2xl font-bold text-slate-900 mb-2">Lista não encontrada</h2>
                <p className="text-slate-600">{state.error}</p>
                <p className="text-sm text-slate-400 mt-4">ID consultado: <span className="font-mono">{id}</span></p>
              </div>
            )}

            {!state.loading && state.data && (
              <div data-testid="list-validation-valid">
                <div className="flex flex-col items-center text-center mb-6">
                  <CheckCircle2 className="w-16 h-16 text-emerald-500 mb-2" />
                  <h2 className="text-2xl font-bold text-slate-900">Lista autêntica</h2>
                  <p className="text-slate-500 text-sm">Emitida por AVI Segurança</p>
                </div>

                <dl className="divide-y divide-slate-200 border border-slate-200 rounded-lg overflow-hidden mb-6">
                  <Row icon={Award} label="Treinamento" value={
                    COURSE_LABELS[state.data.course_key] || state.data.course_titulo || state.data.course_key
                  } testId="list-course" />
                  <Row icon={Calendar} label="Data do Treinamento" value={state.data.training_date} testId="list-date" />
                  {state.data.carga_horaria && (
                    <Row icon={Clock} label="Carga Horária" value={state.data.carga_horaria} testId="list-load" />
                  )}
                  {state.data.instrutor && (
                    <Row icon={User} label="Instrutor" value={state.data.instrutor} testId="list-instructor" />
                  )}
                  <Row icon={Users} label="Total de Participantes" value={state.data.count} testId="list-count" />
                  <Row icon={Calendar} label="Lista emitida em" value={formatDate(state.data.created_at)} testId="list-created" />
                </dl>

                <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                  <Users className="w-4 h-4" /> Participantes ({state.data.participants?.length || 0})
                </h3>
                <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="list-participants">
                  <ul className="divide-y divide-slate-100 max-h-72 overflow-y-auto text-sm">
                    {(state.data.participants || []).map((p, i) => (
                      <li key={`${p.nome}-${i}`} className="px-4 py-2 flex justify-between gap-3">
                        <span className="font-medium text-slate-800">{p.nome}</span>
                        <span className="font-mono text-slate-500 text-xs whitespace-nowrap">{p.cpf_masked}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {state.data.certificates?.length > 0 && (
                  <>
                    <h3 className="text-sm font-semibold text-slate-700 mt-6 mb-2 flex items-center gap-2">
                      <Award className="w-4 h-4" /> Certificados emitidos ({state.data.certificates.length})
                    </h3>
                    <div className="border border-emerald-200 rounded-lg overflow-hidden bg-emerald-50/40" data-testid="list-certs">
                      <ul className="divide-y divide-emerald-100 max-h-60 overflow-y-auto text-sm">
                        {state.data.certificates.map((c) => (
                          <li key={c.cert_id} className="px-4 py-2 flex justify-between items-center gap-3">
                            <span className="text-slate-800 truncate">{c.nome}</span>
                            <Link
                              to={`/verificar/${c.cert_id}`}
                              className="font-mono text-xs text-indigo-600 hover:text-indigo-800 whitespace-nowrap"
                              data-testid={`list-cert-link-${c.cert_id}`}
                            >
                              {c.cert_id} →
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </>
                )}
              </div>
            )}

            <div className="mt-8 pt-6 border-t border-slate-100 text-center text-xs text-slate-400">
              <Link to="/" className="hover:text-slate-600" data-testid="list-back-link">
                Voltar ao painel AVI Segurança
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, value, testId }) {
  return (
    <div className="grid grid-cols-3 gap-3 px-4 py-3" data-testid={testId}>
      <dt className="text-sm text-slate-500 flex items-center gap-2">
        <Icon className="w-4 h-4" />
        {label}
      </dt>
      <dd className="col-span-2 text-sm text-slate-900 font-medium">{value || "—"}</dd>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("pt-BR", { dateStyle: "long", timeStyle: "short" });
  } catch {
    return iso;
  }
}
